import { useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { ProjectService } from "../services/projectService";

// Batch delay in ms - accumulate updates before processing
const BATCH_DELAY_MS = 1000;

/**
 * Custom hook for real-time project updates with batched processing
 * @param {function} onProjectUpdate - Callback when a project is updated
 * @param {boolean} enabled - Whether the subscription is enabled
 */
export const useRealtimeProjects = (onProjectUpdate, enabled = true) => {
  // Refs for batching updates
  const pendingUpdates = useRef(new Map()); // Map of projectId -> payload
  const batchTimeout = useRef(null);

  // Process all pending updates in a single batch
  const processBatch = useCallback(async () => {
    const updates = new Map(pendingUpdates.current);
    pendingUpdates.current.clear();

    if (updates.size === 0) return;

    // Fetch all updated projects in a single query
    const projectIds = Array.from(updates.keys());

    try {
      const { data, error } = await supabase
        .from("projects")
        .select(
          `
          *,
          clients (id, name),
          contact:contacts (id, name, email, phone_number, role, client_id),
          ready_for_employee:employees!ready_for_employee_id (id, name, email),
          model_builder_employee:employees!model_builder_id (id, name, email)
        `,
        )
        .in("id", projectIds);

      if (error) {
        console.error("Realtime: Error fetching project batch:", error);
        throw error;
      }

      if (data) {
        // Transform and update each project
        data.forEach((projectData) => {
          const transformedProject = ProjectService.transformProjectData(projectData);
          onProjectUpdate(transformedProject);
        });
      }
    } catch (error) {
      console.error("Realtime: Batch fetch failed, using fallback:", error);

      // Fallback: use payload data directly for each update
      updates.forEach((payload, projectId) => {
        const updatedProject = {
          id: projectId,
          projectName: payload.new.project_name || payload.new.name,
          jobNumber: payload.new.job_number,
          rev: payload.new.rev,
          clientName: payload.new.client_name,
          contactName: payload.new.contact_name,
          modelingStatus: payload.new.modeling_status,
          modelingState: payload.new.modeling_state,
          isHighPriority: payload.new.is_high_priority,
          isSoftHold: payload.new.is_soft_hold,
          softHoldDate: payload.new.soft_hold_date,
          dueDate: payload.new.due_date,
          notes: payload.new.notes,
          createdAt: payload.new.created_at,
          updatedAt: payload.new.updated_at,
        };
        onProjectUpdate(updatedProject);
      });
    }
  }, [onProjectUpdate]);

  // Queue an update for batched processing
  const queueUpdate = useCallback(
    (payload) => {
      if (payload.eventType !== "UPDATE" || !payload.new) {
        return;
      }

      // Store/overwrite the update for this project (latest wins)
      pendingUpdates.current.set(payload.new.id, payload);

      // Reset the batch timer
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      batchTimeout.current = setTimeout(processBatch, BATCH_DELAY_MS);
    },
    [processBatch],
  );

  useEffect(() => {
    if (!enabled || !onProjectUpdate) {
      return;
    }

    // Subscribe to changes on the projects table
    const channel = supabase
      .channel("projects-realtime-main")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "projects",
        },
        queueUpdate,
      )
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR") {
          console.error("Realtime: Failed to subscribe to projects");
        }
      });

    // Cleanup subscription on unmount
    return () => {
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      supabase.removeChannel(channel);
    };
  }, [enabled, queueUpdate, onProjectUpdate]);
};
